/**
 * Class Reminder Service
 * Handles scheduling and managing class notifications
 */

export interface ClassReminder {
  id: string;
  classId: string;
  courseName: string;
  instructor: string;
  location: string;
  startTime: string;
  day: string;
  reminderTime: number; // minutes before class
  scheduledFor: Date;
  isScheduled: boolean;
  timeoutId?: number;
}

export class ReminderService {
  private static instance: ReminderService;
  private reminders: Map<string, ClassReminder> = new Map();
  private checkInterval?: number;

  private constructor() {
    // Private constructor for singleton pattern
  }

  public static getInstance(): ReminderService {
    if (!ReminderService.instance) {
      ReminderService.instance = new ReminderService();
    }
    return ReminderService.instance;
  }

  /**
   * Initialize the reminder service
   */
  public initialize() {
    // Check for due reminders every minute
    this.checkInterval = window.setInterval(() => {
      this.checkDueReminders();
    }, 60000); // Check every minute

    console.log('Reminder service initialized');
  }

  /**
   * Cleanup when service is destroyed
   */
  public cleanup() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
    
    // Clear all scheduled timeouts
    this.reminders.forEach(reminder => {
      if (reminder.timeoutId) {
        clearTimeout(reminder.timeoutId);
      }
    });
    
    this.reminders.clear();
    console.log('Reminder service cleaned up');
  }

  /**
   * Schedule reminders for user's classes
   */
  public scheduleReminders(
    userProgram: any,
    reminderSettings: {
      classReminders: boolean;
      reminderTime: number;
      pushNotifications: boolean;
      soundEnabled: boolean;
    }
  ) {
    console.log('📅 Scheduling reminders with settings:', reminderSettings);
    
    if (!reminderSettings.classReminders) {
      console.log('⚠️ Class reminders are disabled in settings');
      this.clearAllReminders();
      return;
    }

    if (!reminderSettings.pushNotifications) {
      console.log('⚠️ Push notifications are disabled in settings');
      this.clearAllReminders();
      return;
    }

    // Check notification permission
    if (!('Notification' in window)) {
      console.error('❌ Notifications not supported in this browser');
      return;
    }

    if (Notification.permission !== 'granted') {
      console.warn('⚠️ Notification permission not granted. Current status:', Notification.permission);
      return;
    }

    this.clearAllReminders();
    const today = new Date();
    const currentDay = today.toLocaleDateString('en-US', { weekday: 'long' });

    console.log(`📆 Current day: ${currentDay}`);
    console.log(`📚 User program:`, userProgram.program);

    // Schedule for today and tomorrow
    const daysToSchedule = [
      currentDay,
      this.getNextDay(currentDay)
    ];

    let scheduledCount = 0;
    daysToSchedule.forEach(day => {
      const daySchedule = userProgram.schedule[day];
      if (daySchedule && Array.isArray(daySchedule)) {
        console.log(`📋 Found ${daySchedule.length} classes for ${day}`);
        daySchedule.forEach((course: any, index: number) => {
          const success = this.scheduleClassReminder(course, day, index, reminderSettings);
          if (success) scheduledCount++;
        });
      } else {
        console.log(`📋 No classes found for ${day}`);
      }
    });

    console.log(`✅ Successfully scheduled ${scheduledCount} class reminders out of ${this.reminders.size} total`);
    
    if (scheduledCount === 0) {
      console.warn('⚠️ No reminders were scheduled. This might be because:');
      console.warn('  - All classes have already passed for today');
      console.warn('  - No classes scheduled for today/tomorrow');
      console.warn('  - Time parsing failed');
    }
  }

  /**
   * Schedule a single class reminder
   */
  private scheduleClassReminder(
    course: any,
    day: string,
    index: number,
    settings: { reminderTime: number; soundEnabled: boolean }
  ): boolean {
    const classId = `${day}-${index}-${course.course_name || course.course}`;
    const courseName = course.course_name || course.course;
    const instructor = course.instructor || course.teacher || 'TBA';
    const location = course.location || 'TBA';
    const timeString = course.time;

    if (!timeString || !courseName) {
      console.warn(`⚠️ Skipping class - missing time or name:`, course);
      return false;
    }

    // Calculate when to send the reminder
    const classDateTime = this.getClassDateTime(day, timeString);
    if (!classDateTime) {
      console.warn(`⚠️ Could not parse time for ${courseName}: ${timeString}`);
      return false;
    }

    const reminderDateTime = new Date(classDateTime.getTime() - (settings.reminderTime * 60 * 1000));
    const now = new Date();

    // Only schedule if reminder time is in the future
    if (reminderDateTime <= now) {
      console.log(`⏭️ Skipping ${courseName} - reminder time has passed (${reminderDateTime.toLocaleTimeString()})`);
      return false;
    }

    const reminder: ClassReminder = {
      id: `reminder-${classId}`,
      classId,
      courseName,
      instructor,
      location,
      startTime: timeString,
      day,
      reminderTime: settings.reminderTime,
      scheduledFor: reminderDateTime,
      isScheduled: true
    };

    // Schedule the notification
    const timeoutDuration = reminderDateTime.getTime() - now.getTime();
    
    reminder.timeoutId = window.setTimeout(() => {
      console.log(`🔔 Sending reminder for ${courseName}`);
      this.sendNotification(reminder, settings.soundEnabled);
      this.reminders.delete(reminder.id);
    }, timeoutDuration);

    this.reminders.set(reminder.id, reminder);
    
    console.log(`✅ Scheduled: ${courseName} on ${day} at ${classDateTime.toLocaleTimeString()} (reminder at ${reminderDateTime.toLocaleTimeString()})`);
    return true;
  }

  /**
   * Get the date/time for a class
   */
  private getClassDateTime(day: string, timeString: string): Date | null {
    try {
      const today = new Date();
      const currentDay = today.toLocaleDateString('en-US', { weekday: 'long' });
      
      // Calculate target date
      let targetDate = new Date(today);
      
      if (day !== currentDay) {
        // If it's not today, assume it's tomorrow (for simplicity)
        targetDate.setDate(today.getDate() + 1);
      }

      // Parse the start time
      const startTimeStr = timeString.split('-')[0]?.trim();
      if (!startTimeStr) return null;

      // Extract time components
      const timeMatch = startTimeStr.match(/(\d+):(\d+)\s*(AM|PM)?/i);
      if (!timeMatch) return null;

      let hours = parseInt(timeMatch[1]);
      const minutes = parseInt(timeMatch[2]);
      const period = timeMatch[3]?.toUpperCase();

      // Convert to 24-hour format
      if (period === 'PM' && hours !== 12) {
        hours += 12;
      } else if (period === 'AM' && hours === 12) {
        hours = 0;
      }

      targetDate.setHours(hours, minutes, 0, 0);
      return targetDate;
    } catch (error) {
      console.error('Error parsing class time:', error);
      return null;
    }
  }

  /**
   * Send a notification for a class reminder
   */
  private sendNotification(reminder: ClassReminder, soundEnabled: boolean) {
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      console.warn('Notifications not available or not permitted');
      return;
    }

    try {
      const notification = new Notification('Class Reminder', {
        body: `${reminder.courseName} starts in ${reminder.reminderTime} minutes\n📍 ${reminder.location}\n👨‍🏫 ${reminder.instructor}`,
        icon: '/icons/android-chrome-192x192.png',
        badge: '/icons/android-chrome-192x192.png',
        tag: `class-reminder-${reminder.classId}`,
        requireInteraction: true,
        data: {
          classId: reminder.classId,
          courseName: reminder.courseName,
          startTime: reminder.startTime,
          location: reminder.location,
          instructor: reminder.instructor
        }
      });

      // Play sound if enabled
      if (soundEnabled) {
        this.playNotificationSound();
      }

      // Auto-close after 10 seconds if not interacted with
      setTimeout(() => {
        notification.close();
      }, 10000);

      // Handle notification click
      notification.onclick = () => {
        window.focus();
        notification.close();
        
        // Navigate to personal timetable
        if (window.location.pathname !== '/dashboard/personal') {
          window.location.href = '/dashboard/personal';
        }
      };

      console.log(`Sent reminder notification for ${reminder.courseName}`);
    } catch (error) {
      console.error('Error sending notification:', error);
    }
  }

  /**
   * Play notification sound
   */
  private playNotificationSound() {
    try {
      // Create a simple notification sound
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.warn('Could not play notification sound:', error);
    }
  }

  /**
   * Check for due reminders (backup mechanism)
   */
  private checkDueReminders() {
    const now = new Date();
    
    this.reminders.forEach((reminder, id) => {
      if (reminder.scheduledFor <= now) {
        this.sendNotification(reminder, true); // Assume sound enabled for backup checks
        this.reminders.delete(id);
        
        if (reminder.timeoutId) {
          clearTimeout(reminder.timeoutId);
        }
      }
    });
  }

  /**
   * Clear all scheduled reminders
   */
  private clearAllReminders() {
    this.reminders.forEach(reminder => {
      if (reminder.timeoutId) {
        clearTimeout(reminder.timeoutId);
      }
    });
    this.reminders.clear();
    console.log('Cleared all reminders');
  }

  /**
   * Get the next day of the week
   */
  private getNextDay(currentDay: string): string {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const currentIndex = days.indexOf(currentDay);
    const nextIndex = (currentIndex + 1) % 7;
    return days[nextIndex];
  }

  /**
   * Get current status of reminders
   */
  public getStatus() {
    return {
      totalReminders: this.reminders.size,
      reminders: Array.from(this.reminders.values()).map(r => ({
        courseName: r.courseName,
        day: r.day,
        startTime: r.startTime,
        scheduledFor: r.scheduledFor.toLocaleString(),
        reminderTime: r.reminderTime
      }))
    };
  }

  /**
   * Test notification (for settings)
   */
  public sendTestReminder(courseName: string = 'Database Systems', reminderTime: number = 5) {
    if (!('Notification' in window)) {
      alert('Notifications are not supported in your browser');
      return false;
    }

    if (Notification.permission !== 'granted') {
      alert('Please grant notification permission first');
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          this.sendTestReminder(courseName, reminderTime);
        }
      });
      return false;
    }

    try {
      const notification = new Notification('Test Class Reminder ✅', {
        body: `${courseName} starts in ${reminderTime} minutes\n📍 Room LR-101\n👨‍🏫 Dr. Smith`,
        icon: '/icons/android-chrome-192x192.png',
        badge: '/icons/android-chrome-192x192.png',
        tag: 'test-reminder',
        requireInteraction: false
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      setTimeout(() => {
        notification.close();
      }, 5000);

      console.log('✅ Test reminder sent successfully');
      return true;
    } catch (error) {
      console.error('❌ Failed to send test reminder:', error);
      alert(`Failed to send notification: ${error}`);
      return false;
    }
  }
}